import { useForm } from 'react-hook-form';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import _ from 'lodash';

import { PaymentsCopy, PaymentsCountries, PaymentsMethods, useOffers } from 'features/offers';
import {
  Box,
  Button,
  Flex,
  HelperText,
  Icons,
  Input,
  Label,
  Typography,
  Hr,
  Row,
  Select,
  Col,
  Loading,
  InputSuggest,
  ButtonIcon,
  Checkbox,
} from 'components';
import {
  phoneMask,
  brazilianRealFormat,
  cepMask,
  cpfCnpjMask,
  CpfCnpjUtils,
  createUrl,
  creditCardFlag,
  dateMask2,
  request,
  formatDate,
  deviceCheck,
  createMarkup,
  getAddressByZipcode,
  nameMatch,
} from 'app/utils';
import { AxiosError, AxiosResponse } from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from 'features/auth';
import {
  CheckoutState,
  OfferConclusionPageTypeEnum,
  OfferImageLayoutEnum,
  PaymentsApiResult,
  PaymentsLeadApiResult,
  PaymentsMethod,
  PaymentSteps,
  PaymentValuesItem,
} from 'features/offers/types';
import { AccountItemState, CustomersTypes } from 'features/account';
import moment from 'moment';
import { useAlert } from 'features/alert';
import { useSchool } from 'features/school';

import LayoutsAccount from 'features/offers/components/account/LayoutsAccount';
import { useTracking } from 'features/tracking';

// const InternationalContactPhoneWrapper = styled.label`
//   display: flex;
//   align-items: center;
//   flex-wrap: nowrap;
//   cursor: pointer;
//   margin-left: 10px;
// `;

const CreditCardFlag = styled.img`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 6px;
`;

const DescriptionCollapse = styled.span`
  color: ${({ theme }) => theme.colors.primary_400};
  text-decoration: underline;
  margin-left: 2px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.primary_200};
  }
`;

export const WarrantyIcon = styled(Icons)`
  color: ${({ theme }) => theme.colors.dark_350};
  flex-shrink: 0;

  ${({ theme: { mediaQueries } }) => {
    return `
      width: 35px;

      ${mediaQueries.desktop} {
        width: 35px;
      }
    `;
  }}
`;

export const LockIcon = styled(Icons)`
  color: ${({ theme }) => theme.colors.dark_350};
  flex-shrink: 0;

  ${({ theme: { mediaQueries } }) => {
    return `
      width: 35px;

      ${mediaQueries.desktop} {
        width: 35px;
      }
    `;
  }}
`;

const Icon = styled(Icons)`
  color: ${({ theme }) => theme.colors.primary_400};

  ${({ theme: { mediaQueries } }) => {
    return `
      width: 25px;

      ${mediaQueries.desktop} {
        width: 35px;
      }
    `;
  }}
`;

const LinkTerms = styled.a`
  color: ${({ theme }) => theme.colors.dark_350};
  text-decoration: underline;
`;

const Checkout = () => {
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [, setUpdate] = useState(new Date());
  const [collapseDescription, setCollapseDescription] = useState(false);
  // const [, setInitiateCheckout] = useState(false);

  const navigate = useNavigate();
  const { offerIdentifier } = useParams();
  const [searchParams] = useSearchParams();
  const { isLogged, customerUuid } = useAuth();
  const { setAlert, alert: melverAlert } = useAlert();
  const {
    setCheckout,
    checkout,
    buyInformation,
    showLoginMessage,
    bypassLogin,
    setLogin,
    activeCampaingIds,
    setPaymentsResponse,
    getOfferLink,
    captcha,
    getCaptcha,
  } = useOffers();
  const { schoolAlias, apiGatewayUrl, schoolMelver, schoolUuid } = useSchool();
  const { eventTracker } = useTracking();

  const marketingSource = searchParams.get('src');

  const isProgramXP = nameMatch(buyInformation?.offerName, ['assessores', 'xp']);

  // const isProgramXP =
  //   buyInformation?.offerName.toLowerCase().search('assessores') !== -1 && buyInformation?.offerName.toLowerCase().search('xp') !== -1;

  const schema = yup
    .object()
    .shape({
      customerName: yup.string().when('paymentStep', {
        is: 'payment',
        then: yup
          .string()
          .required('Você precisa informar o nome completo')
          .test('min-words', 'Você precisa informar o nome completo', (value) => {
            if (!value) return false;

            const words = value.split(' ');

            return words.length >= 2;
          }),
      }),

      customerContactEmail: yup.string().when('paymentStep', {
        is: 'payment',
        then: yup.string().required('Você precisa informar o e-mail').email('O e-mail informado está inválido'),
      }),

      customerContactEmailConfirm: yup.string().when('withEMailConfirmation', {
        is: true,
        then: yup
          .string()
          .required('Você precisa repetir o e-mail')
          .email('O e-mail informado está inválido')
          .oneOf([yup.ref('customerContactEmail'), null], 'Os e-mails informados estão diferentes'),
      }),

      customerContactPhone: yup.string().when('withPhone', {
        is: true,
        then: yup.string().required('Você precisa informar o celular'),
      }),

      // Payments

      creditCardNumber: yup.string().when('paymentStep', {
        is: 'payment',
        then: yup.string().when('paymentMethod', {
          is: 'creditCard',
          then: yup.string().required('Informe o número do cartão'),
        }),
      }),

      creditCardHolderName: yup.string().when('paymentStep', {
        is: 'payment',
        then: yup.string().when('paymentMethod', {
          is: 'creditCard',
          then: yup
            .string()
            .required('informe o nome do titular do cartão')
            .test('', 'Você precisa informar o nome completo', (value) => {
              if (!value) return false;

              return value.trim().split(/\s+/).length >= 2;
            }),
        }),
      }),

      creditCardExpirationMonth: yup.number().when('paymentStep', {
        is: 'payment',
        then: yup.number().when('paymentMethod', {
          is: 'creditCard',
          then: yup.number().positive('Informe a validade do cartão').required('Informe a validade do cartão'),
        }),
      }),

      creditCardExpirationYear: yup.number().when('paymentStep', {
        is: 'payment',
        then: yup.number().when('paymentMethod', {
          is: 'creditCard',
          then: yup.number().positive('Informe a validade do cartão').required('Informe a validade do cartão'),
        }),
      }),

      creditCardSecurityCode: yup.string().when('paymentStep', {
        is: 'payment',
        then: yup.string().when('paymentMethod', {
          is: 'creditCard',
          then: yup.string().required('Informe o código de segurança'),
        }),
      }),

      installments: yup.number().when('paymentStep', {
        is: 'payment',
        then: yup.number().required('Você precisa informar o número de parcelas'),
      }),

      customerTaxId: yup.string().when('withTaxId', {
        is: true,
        then: yup.string().when('internationalContactPhone', {
          is: false,
          then: yup
            .string()
            .required('Você precisa informar o CPF ou CNPJ')
            .test(
              'test-cpfCnpj',
              checkout.customerType === CustomersTypes.Person ? 'O CPF informado está inválido' : 'O CNPJ informado está inválido',
              function (value, context) {
                const cpfCnpj = `${value}`.replace(/[^\d]+/g, '');

                if (cpfCnpj.length === 14) {
                  return CpfCnpjUtils.isCnpjValid(cpfCnpj);
                } else {
                  return CpfCnpjUtils.isCpfValid(cpfCnpj);
                }
              }
            ),
        }),
      }),

      customerBirthOrFoundation: yup.string().when('withBirthDate', {
        is: true,
        then: yup
          .string()
          .required(
            `Você precisa informar ${checkout.customerType === CustomersTypes.Person ? 'sua data de nascimento' : 'a data de abertura da empresa'}`
          )
          .test(
            'test-birthOrFoundation',
            `A ${checkout.customerType === CustomersTypes.Person ? 'data de nascimento' : 'data de abertura da empresa'} está inválida`,
            function (value) {
              const birthDate = moment(value, 'DD/MM/YYYY', true);

              return birthDate.isValid();
            }
          ),
      }),

      customerZipCode: yup.string().when('showAddress', {
        is: true,
        then: yup.string().when('internationalContactPhone', {
          is: false,
          then: yup
            .string()
            .required(`Informe seu CEP`)
            .test('min-length', 'O CEP informado está incompleto', (value) => {
              if (!value) return false;

              return value.length === 9;
            }),
        }),
      }),

      customerAddressLine1: yup.string().when('showAddress', {
        is: true,
        then: yup.string().when('internationalContactPhone', {
          is: false,
          then: yup.string().required(`Informe seu endereço`),
        }),
      }),

      customerAddressNumber: yup.string().when('showAddress', {
        is: true,
        then: yup.string().when('internationalContactPhone', {
          is: false,
          then: yup.string().required(`Informe o número`),
        }),
      }),

      customerNeighborhood: yup.string().when('showAddress', {
        is: true,
        then: yup.string().when('internationalContactPhone', {
          is: false,
          then: yup.string().required(`Informe seu bairro`),
        }),
      }),

      customerCity: yup.string().when('showAddress', {
        is: true,
        then: yup.string().when('internationalContactPhone', {
          is: false,
          then: yup.string().required(`Informe sua cidade`),
        }),
      }),

      customerState: yup.string().when('showAddress', {
        is: true,
        then: yup.string().when('internationalContactPhone', {
          is: false,
          then: yup.string().required(`Informe a UF`),
        }),
      }),

      captchaText: yup.string().when('disableCaptcha', {
        is: false,
        then: yup.string().required(`Insira os caracteres exibidos na imagem`),
      }),

      hasOptin: yup.boolean(),

      optin: yup.boolean().when('hasOptin', {
        is: true,
        then: yup.boolean().oneOf([true], 'Você precisa ler e aceitar o termo de adesão e a política de privacidade.'),
      }),
    })
    .required();

  const paymentUrl = getOfferLink(offerIdentifier);

  const {
    handleSubmit,
    register,
    getValues,
    formState: { errors },
    setValue,
    setFocus,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: useMemo(() => {
      return {
        leadUuid: checkout.leadUuid,
        paymentStep: checkout.paymentStep,

        customerUuid: customerUuid || '',

        customerName: checkout.customerName,
        customerContactEmail: checkout.customerContactEmail,
        customerContactEmailConfirm: checkout.customerContactEmailConfirm,
        customerContactPhone: checkout.customerContactPhone
          ? !checkout.internationalContactPhone
            ? phoneMask(checkout.customerContactPhone)
            : checkout.customerContactPhone
          : '',
        internationalContactPhone: checkout.internationalContactPhone,

        customerType: checkout.customerType,
        customerTaxId: checkout.customerTaxId,
        customerBirthOrFoundation: checkout.customerBirthOrFoundation,

        // Address
        customerZipCode: checkout.customerZipCode,
        customerAddressLine1: checkout.customerAddressLine1,
        customerAddressNumber: checkout.customerAddressNumber,
        customerAddressLine2: checkout.customerAddressLine2,
        customerNeighborhood: checkout.customerNeighborhood,
        customerCity: checkout.customerCity,
        customerState: checkout.customerState,
        customerCountry: checkout.customerCountry,

        // Payment
        paymentMethod: checkout.paymentMethod,
        creditCardNumber: checkout.creditCardNumber,
        creditCardSecurityCode: checkout.creditCardSecurityCode,
        creditCardHolderName: checkout.creditCardHolderName,
        creditCardExpirationMonth: checkout.creditCardExpirationMonth,
        creditCardExpirationYear: checkout.creditCardExpirationYear,
        installments: checkout.installments,
        value: checkout.value,

        withBirthDate: checkout.withBirthDate,
        withAddress: checkout.withAddress,
        showAddress: checkout.paymentMethod === 'boleto' || checkout.withAddress,

        withTaxId: checkout.withTaxId,
        withEMailConfirmation: checkout.withEMailConfirmation,
        withPhone: checkout.withPhone,

        disableCaptcha: checkout.disableCaptcha,
        captchaText: '',

        hasOptin: isProgramXP,
        optin: false,
      };
    }, [checkout, customerUuid, isProgramXP, captcha]),
  });

  const formData = getValues();
  const {
    withBirthDate,
    withAddress,
    showAddress,

    withTaxId,
    withEMailConfirmation,
    withPhone,

    // customerName,
    customerContactEmail,
    customerContactEmailConfirm,
    // customerContactPhone,
    internationalContactPhone,

    customerType,
    // customerTaxId,
    // customerBirthOrFoundation,
    // Address
    // customerZipCode,
    // customerAddressLine1,
    // customerAddressNumber,
    // customerAddressLine2,
    // customerNeighborhood,
    // customerCity,
    // customerState,
    // customerCountry,

    // Payment
    paymentMethod,
    creditCardNumber,
    // creditCardSecurityCode,
    // creditCardHolderName,
    creditCardExpirationMonth,
    creditCardExpirationYear,
    installments,
    // value,

    disableCaptcha,
    hasOptin,
    optin,
  } = formData;

  const creditCardBrand = creditCardFlag(creditCardNumber);

  const sendLead = useCallback(
    async (data: Partial<CheckoutState>, openLogin: boolean = false, sendAllData: boolean = true) => {
      const newFormData = { ...formData, ...data };

      Object.keys(data).forEach((key) => {
        const checkoutField = key as keyof CheckoutState;
        let checkoutValue = data[checkoutField];

        setValue(checkoutField as any, checkoutValue, { shouldValidate: true });
      });

      if (newFormData.customerContactEmail) {
        let leadData: any = {
          channel: 'web',
          uuid: newFormData.leadUuid || undefined,
          name: newFormData.customerName || undefined,
          phone: newFormData.customerContactPhone || undefined,
          contactEmail: newFormData.customerContactEmail || undefined,
          src: marketingSource || undefined,
        };

        if (sendAllData) {
          leadData = {
            ...leadData,
            country: newFormData.customerCountry || undefined,
            paymentMethod: newFormData.paymentMethod || undefined,
            installments: newFormData.installments || undefined,
            value: newFormData.value || undefined,
            birth: newFormData.customerBirthOrFoundation ? moment(newFormData.customerBirthOrFoundation, 'DD/MM/YYYY').toISOString() : undefined,
            taxId: newFormData.customerTaxId || undefined,
            state: newFormData.customerState || undefined,
            city: newFormData.customerCity || undefined,
            ...deviceCheck(),
          };
        }

        await request({
          url: `/api/v1/School/OfferLeads/${schoolAlias}/${buyInformation?.offerUuid}`,
          method: newFormData.leadUuid ? 'put' : 'post',
          baseURL: apiGatewayUrl,
          data: leadData,
        }).then((response: AxiosResponse<PaymentsLeadApiResult>) => {
          const { uuid, isStudent } = response?.data?.apiResultData;

          if (!newFormData.leadUuid) {
            setValue('leadUuid', uuid, { shouldValidate: true });
          }

          if (!isLogged && isStudent && openLogin && !bypassLogin) {
            setLogin({ showLoginMessage: true, hasAccount: true });
          } else {
            setLogin({ showLoginMessage: false, hasAccount: false });
          }
        });
      }
    },
    [formData, schoolAlias, apiGatewayUrl, isLogged, bypassLogin, buyInformation, marketingSource]
  );

  const getInstallments = useCallback((): PaymentValuesItem[] => {
    let paymentValues: PaymentValuesItem[] = undefined!;

    switch (paymentMethod) {
      case 'creditCard':
        paymentValues = buyInformation?.creditCardValues;
        break;

      case 'boleto':
        paymentValues = buyInformation?.boletoValues;
        break;

      case 'pix':
        paymentValues = buyInformation?.pixValues;
        break;
    }

    return paymentValues;
  }, [paymentMethod, buyInformation]);

  const getInstallmentsValue = useCallback((): number => {
    switch (paymentMethod) {
      case 'creditCard':
        const creditCardOpt = _.find(buyInformation?.creditCardValues, (o) => o.installments === installments);
        return creditCardOpt?.installmentValue || 0;

      case 'boleto':
        const boletoValuesOpt = _.find(buyInformation?.boletoValues, (o) => o.installments === installments);
        return boletoValuesOpt?.installmentValue || 0;

      case 'pix':
        const pixValuesOpt = _.find(buyInformation?.pixValues, (o) => o.installments === installments);
        return pixValuesOpt?.installmentValue || 0;

      case 'free':
        return 0;
    }
  }, [paymentMethod, buyInformation, installments]);

  let submitLabel = '';

  switch (paymentMethod) {
    case 'boleto':
      submitLabel = 'Gerar boleto';
      break;

    case 'pix':
      submitLabel = 'Pagar com PIX';
      break;

    case 'creditCard':
      submitLabel = 'Comprar agora';
      break;

    case 'free':
      submitLabel = 'Realizar matrícula';
      break;
  }

  const goToStep = (step: PaymentSteps) => {
    setValue('paymentStep', step, { shouldValidate: true });
    setCheckout({ ...getValues(), paymentStep: step });
  };

  // const activeCampaingAddContactToList = useCallback(
  //   async (listId: number, data: Partial<CheckoutState> = {}) => {
  //     const newData = { ...formData, ...data };

  //     const { customerName, customerContactEmail, customerContactPhone } = newData;

  //     var fullName = customerName.split(' '),
  //       firstName = fullName[0],
  //       lastName = fullName[fullName.length - 1];

  //     await axios.post(`https://raonyrossetti.com/ac/`, {
  //       firstName,
  //       lastName,
  //       email: customerContactEmail,
  //       phone: customerContactPhone,
  //       listId,
  //     });
  //   },
  //   [formData]
  // );

  // const activeCampaingAddTagInContact = useCallback(
  //   async (tagId: number) => {
  //     const { customerName, customerContactEmail, customerContactPhone } = formData;

  //     var fullName = customerName.split(' '),
  //       firstName = fullName[0],
  //       lastName = fullName[fullName.length - 1];

  //     return await axios.post(`https://raonyrossetti.com/ac/`, {
  //       firstName,
  //       lastName,
  //       email: customerContactEmail,
  //       phone: customerContactPhone,
  //       tagId,
  //     });
  //   },
  //   [formData]
  // );

  const getAccount = useCallback(async () => {
    await request({
      url: `/api/v1/School/MyAccount`,
      method: 'get',
      baseURL: apiGatewayUrl,
    })
      .then((response: AxiosResponse<AccountItemState>) => {
        const {
          customerUuid,
          name,
          contactEmail,
          contactPhone,
          internationalContactPhone,
          birthOrFoundation: receivedBirthOrFoundation,
          taxId,
          addressZipCode,
          addressLine1,
          addressNumber,
          addressLine2,
          addressNeighborhood,
          addressCity,
          addressState,
          customerType,
        } = response.data;

        const customerContactPhone = contactPhone ? (!internationalContactPhone ? phoneMask(String(contactPhone)) : contactPhone) : '';

        const customerBirthOrFoundation = receivedBirthOrFoundation ? formatDate(receivedBirthOrFoundation).utc().format('DD/MM/YYYY') : '';

        const customerZipCode = addressZipCode ? cepMask(addressZipCode) : '';

        const customerTaxId = taxId ? String(cpfCnpjMask(taxId)) : '';

        const paymentStep: PaymentSteps = 'payment';

        const newCheckout = {
          customerUuid,
          customerName: name || '',
          customerContactEmail: contactEmail || '',
          customerContactEmailConfirm: contactEmail || '',
          customerBirthOrFoundation,
          customerTaxId,
          customerContactPhone,
          internationalContactPhone: Boolean(internationalContactPhone),
          customerAddressLine1: addressLine1 || '',
          customerAddressNumber: addressNumber || '',
          customerAddressLine2: addressLine2 || '',
          customerNeighborhood: addressNeighborhood || '',
          customerZipCode,
          customerCity: addressCity || '',
          customerState: addressState || '',
          customerCountry: 'BR',
          customerType,
          paymentStep,
        };

        sendLead(newCheckout, false, true);

        // if (activeCampaingIds?.addContactToList) {
        //   activeCampaingAddContactToList(activeCampaingIds?.addContactToList, newCheckout);
        // }
      })
      .finally(() => {});
  }, [activeCampaingIds]);

  useEffect(() => {
    if (isLogged) getAccount();
  }, [isLogged]);

  useEffect(() => {
    const isInternational = checkout?.customerCountry !== 'BR';

    setValue('internationalContactPhone', isInternational, {
      shouldValidate: true,
    });

    setValue('customerCountry', checkout?.customerCountry, { shouldValidate: true });

    if (isInternational) {
      let currentCustomerType = CustomersTypes.Person;
      setValue('customerType', currentCustomerType, { shouldValidate: true });
      setCheckout({ customerType: currentCustomerType });

      if (!buyInformation.freeOffer && buyInformation?.creditCardValues?.length) {
        const methodInformationInter = buyInformation?.creditCardValues[0];
        sendLead({
          paymentMethod: 'creditCard',
          installments: methodInformationInter.installments,
          value: methodInformationInter.totalValue,
        });
      }
    } else {
      if (!buyInformation.freeOffer && buyInformation?.creditCardValues?.length) {
        const methodInformation = buyInformation?.creditCardValues[buyInformation?.creditCardValues?.length - 1];
        sendLead({
          paymentMethod: 'creditCard',
          installments: methodInformation.installments,
          value: methodInformation.totalValue,
        });
      }
    }
  }, [checkout?.customerCountry]);

  let value = 0;
  let totalValue = 0;
  let checkoudInstallments = 1;

  if (!!buyInformation?.creditCardValues?.length) {
    checkoudInstallments = buyInformation?.creditCardValues?.length;
    value = buyInformation?.creditCardValues[buyInformation?.creditCardValues.length - 1].installmentValue;
    totalValue = buyInformation?.creditCardValues[0].totalValue;
  } else if (!!buyInformation?.boletoValues?.length) {
    checkoudInstallments = buyInformation?.boletoValues?.length;
    value = buyInformation?.boletoValues[buyInformation?.boletoValues.length - 1].installmentValue;
    totalValue = buyInformation?.boletoValues[0].totalValue;
  } else if (!!buyInformation?.pixValues?.length) {
    checkoudInstallments = buyInformation?.pixValues?.length;
    value = buyInformation?.pixValues[buyInformation?.pixValues.length - 1].installmentValue;
    totalValue = buyInformation?.pixValues[0].totalValue;
  }

  // const showValue = value !== 0;

  const [conversionLabel, setConversionLabel] = useState('');

  const schoolG10 = schoolUuid === '14d970bd-3794-4268-bf67-2c5d2d0f0285';

  useEffect(() => {
    if (schoolMelver) {
      const isMelverCertifica = ['melver', 'certifica'].some((el) => buyInformation?.offerName.toLowerCase().split(' ').includes(el));
      const isEtfsNasdaq = ['etfs', 'nasdaq'].some((el) => buyInformation?.offerName.toLowerCase().split(' ').includes(el));
      const isDividendosAtivos = ['dividendos', 'ativos'].some((el) => buyInformation?.offerName.toLowerCase().split(' ').includes(el));
      const isCPA10 = ['cpa', '10'].filter((el) => buyInformation?.offerName.toLowerCase().split('-').includes(el))?.length === 2;
      const isCPA20 = ['cpa', '20'].filter((el) => buyInformation?.offerName.toLowerCase().split('-').includes(el))?.length === 2;

      if (isMelverCertifica) {
        setConversionLabel('OIM6CM_73IwYEJS-iNYD');
      } else if (isEtfsNasdaq) {
        setConversionLabel('vP1GCOPOpo8YEJS-iNYD');
      } else if (isDividendosAtivos) {
        setConversionLabel('jwD-CJvnv5MYEJS-iNYD');
      } else if (isCPA10) {
        setConversionLabel('j_I-CJ68uaMYEJS-iNYD');
      } else if (isCPA20) {
        setConversionLabel('26jbCKG8uaMYEJS-iNYD');
      }
    } else if (schoolG10) {
      const isConselheiroAcoes = ['conselheiro', 'açôes'].some((el) => buyInformation?.offerName.toLowerCase().split(' ').includes(el));

      if (isConselheiroAcoes) {
        setConversionLabel('f4QuCKfNt5kYEK6G7JYB');
      }
    }
  }, [buyInformation]);

  return (
    <Box
      bg="white"
      borderTopLeftRadius={{
        m: buyInformation?.offerImageLayout === OfferImageLayoutEnum.NoImage ? 0 : 4,
        d: 4,
      }}
      borderTopRightRadius={{
        m: buyInformation?.offerImageLayout === OfferImageLayoutEnum.NoImage ? 0 : 4,
        d: 4,
      }}
      borderBottomRightRadius={4}
      borderBottomLeftRadius={4}
      width="100%"
    >
      {loadingEdit && <Loading alpha />}

      {buyInformation?.offerImageLayout === OfferImageLayoutEnum.NoImage ? (
        <Flex display={{ m: 'flex', d: 'none' }} flexDirection="column">
          <Hr color="gray_200" m={0} />

          <Flex bg="white" minHeight={40} alignItems="center" py={2} px={{ m: 3, d: 5 }} justifyContent="space-between">
            <Flex flexGrow={0}>
              <PaymentsCountries />
            </Flex>

            <Flex flexGrow={0} alignItems="center">
              <LayoutsAccount />
            </Flex>
          </Flex>

          <Hr color="gray_200" size="2px" m={0} />

          <Flex bg="gray_100" py={{ m: 3, d: 3 }} px={{ m: 3, d: 5 }} justifyContent="space-between" flexWrap="nowrap">
            <Flex flexGrow={1} justifyContent="center" flexDirection="column">
              <Typography type="type3" fontWeight="bold" lineHeight={1.1} color="dark_350">
                {buyInformation?.offerName}
              </Typography>

              {!!buyInformation?.offerDescription && (
                <Typography type="type0" lineHeight={1.3} color="gray_500" mt={1}>
                  <span
                    dangerouslySetInnerHTML={createMarkup(
                      collapseDescription ? buyInformation?.offerDescription : String(buyInformation?.offerDescription).substring(0, 110)
                    )}
                  />
                  {buyInformation?.offerDescription.length > 110 && (
                    <>
                      {collapseDescription ? ' ' : '...'}
                      <DescriptionCollapse onClick={() => setCollapseDescription(!collapseDescription)}>
                        {collapseDescription ? 'Ver menos' : 'Ver mais'}
                      </DescriptionCollapse>
                    </>
                  )}
                </Typography>
              )}
            </Flex>

            <Flex ml={5} flexGrow={0} flexDirection="column" minWidth={{ m: 80, d: 120 }}>
              <Typography type="type0" color="gray_500" fontWeight="bold" upper>
                Valor
              </Typography>

              {!buyInformation?.freeOffer ? (
                <>
                  {checkoudInstallments === 1 ? (
                    <>
                      <Typography style={{ whiteSpace: 'nowrap' }} type="type3" fontWeight="bold" lineHeight={1.1} color="dark_350" mt={1}>
                        {brazilianRealFormat(value)}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography style={{ whiteSpace: 'nowrap' }} type="type3" fontWeight="bold" lineHeight={1.1} color="dark_350" mt={1}>
                        {checkoudInstallments}x de {brazilianRealFormat(value)}
                      </Typography>

                      {!buyInformation?.isRecurrence && (
                        <Typography style={{ whiteSpace: 'nowrap' }} type="type1" fontWeight="bold" lineHeight={1.3} color="gray_600" mt={1}>
                          ou {brazilianRealFormat(totalValue)} à vista
                        </Typography>
                      )}
                    </>
                  )}
                </>
              ) : (
                <Typography style={{ whiteSpace: 'nowrap' }} type="type3" fontWeight="bold" lineHeight={1.1} color="dark_350" mt={1}>
                  Gratuito
                </Typography>
              )}
            </Flex>
          </Flex>

          <Hr color="gray_200" size="2px" m={0} />
        </Flex>
      ) : (
        <>
          <Flex alignItems="center" minHeight={40} py={2} px={{ m: 3, d: 5 }} justifyContent="space-between">
            <Flex flexGrow={0}>
              <PaymentsCountries />
            </Flex>

            <Flex flexGrow={0} alignItems="center">
              <LayoutsAccount />
            </Flex>
          </Flex>

          <Hr color="gray_200" size="2px" m={0} />

          <Flex bg="gray_100" py={{ m: 3, d: 3 }} px={{ m: 3, d: 5 }} justifyContent="space-between" flexWrap="nowrap">
            <Flex flexGrow={1} justifyContent="center" flexDirection="column">
              <Typography type="type3" fontWeight="bold" lineHeight={1.1} color="dark_350">
                {buyInformation?.offerName}
              </Typography>

              {!!buyInformation?.offerDescription && (
                <Typography type="type0" lineHeight={1.3} color="gray_500" mt={1}>
                  <span
                    dangerouslySetInnerHTML={createMarkup(
                      collapseDescription ? buyInformation?.offerDescription : String(buyInformation?.offerDescription).substring(0, 110)
                    )}
                  />
                  {buyInformation?.offerDescription.length > 110 && (
                    <>
                      {collapseDescription ? ' ' : '...'}
                      <DescriptionCollapse onClick={() => setCollapseDescription(!collapseDescription)}>
                        {collapseDescription ? 'Ver menos' : 'Ver mais'}
                      </DescriptionCollapse>
                    </>
                  )}
                </Typography>
              )}
            </Flex>

            <Flex ml={5} flexGrow={0} flexDirection="column" minWidth={{ m: 80, d: 120 }}>
              <Typography type="type0" color="gray_500" fontWeight="bold" upper>
                Valor
              </Typography>

              {!buyInformation?.freeOffer ? (
                <>
                  {checkoudInstallments === 1 ? (
                    <>
                      <Typography style={{ whiteSpace: 'nowrap' }} type="type3" fontWeight="bold" lineHeight={1.1} color="dark_350" mt={1}>
                        {brazilianRealFormat(value)}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography style={{ whiteSpace: 'nowrap' }} type="type3" fontWeight="bold" lineHeight={1.1} color="dark_350" mt={1}>
                        {checkoudInstallments}x de {brazilianRealFormat(value)}
                      </Typography>

                      {!buyInformation?.isRecurrence && (
                        <Typography style={{ whiteSpace: 'nowrap' }} type="type1" fontWeight="bold" lineHeight={1.3} color="gray_600" mt={1}>
                          ou {brazilianRealFormat(totalValue)} à vista
                        </Typography>
                      )}
                    </>
                  )}
                </>
              ) : (
                <Typography style={{ whiteSpace: 'nowrap' }} type="type3" fontWeight="bold" lineHeight={1.1} color="dark_350" mt={1}>
                  Gratuito
                </Typography>
              )}
            </Flex>
          </Flex>

          <Hr color="gray_200" size="2px" m={0} />
        </>
      )}

      <form
        onSubmit={handleSubmit((data) => {
          // if (activeCampaingIds?.addContactToList) {
          //   activeCampaingAddContactToList(activeCampaingIds?.addContactToList);
          // }

          setLoadingEdit(true);

          const contact = {
            leadUuid: data.leadUuid,
            customerName: data.customerName,
            customerContactEmail: data.customerContactEmail,
            customerContactPhone: data.customerContactPhone,
          };

          const personal = {
            customerTaxId: data.customerTaxId.replace(/\D/g, ''),
            customerBirthOrFoundation: moment(data.customerBirthOrFoundation, 'DD/MM/YYYY').toISOString(),
            customerAddressLine1: data.customerAddressLine1,
            customerAddressNumber: data.customerAddressNumber,
            customerAddressLine2: data.customerAddressLine2,
            customerNeighborhood: data.customerNeighborhood,
            customerZipCode: data.customerZipCode ? data.customerZipCode.replace(/\D/g, '') : data.customerZipCode,
            customerCity: data.customerCity,
            customerState: data.customerState,
            customerCountry: data.customerCountry,
          };

          const marketing = {
            src: marketingSource || undefined,
          };

          const security = {
            captchaUuid: captcha != null ? captcha.captchaUuid || undefined : undefined,
            captchaText: data.captchaText,
          };

          if (data.paymentMethod === 'free') {
            request({
              url: `/api/v1/OfferPayment/Buy/${offerIdentifier}`,
              method: 'post',
              baseURL: apiGatewayUrl,
              data: {
                schoolAlias,

                // Contact
                ...contact,

                // Personal
                ...personal,

                // Marketing
                ...marketing,

                // Security
                ...security,

                // Payment
                installments: 1,
                paymentUrl,
              },
            })
              .then(({ data }: AxiosResponse<PaymentsApiResult>) => {
                const { apiResultData: paymentResponse } = data;

                setPaymentsResponse(paymentResponse);
                goToStep('success');

                eventTracker(
                  {
                    category: 'checkout',
                    action: 'Purchase',
                    value: checkout?.spotValue,
                    transaction_id: paymentResponse?.transactionLocator,
                    product_name: buyInformation?.offerName,
                    customer_email: contact?.customerContactEmail,
                    conversionLabel,
                    schoolAlias,
                  },
                  true
                );

                setTimeout(() => {
                  // TODO: Adicionar upsell
                  if (buyInformation?.offerConclusionPageType === OfferConclusionPageTypeEnum.Default) {
                    navigate(createUrl('offers/success', [offerIdentifier]));
                  } else if (buyInformation?.offerConclusionPageType === OfferConclusionPageTypeEnum.ExternalPage) {
                    window.location.href = buyInformation?.offerExternalUrlCreditCard;
                  }
                }, 10);

                setLoadingEdit(false);
              })
              .catch((error: AxiosError<PaymentsApiResult>) => {
                setLoadingEdit(false);

                let message = 'Ocorreu um erro na comunicação com o serviço de pagamento. Por favor, tente novamente.';

                if (error.response?.data?.apiResultMessage) {
                  message = error.response?.data?.apiResultMessage;
                }

                setAlert({ type: 'error', message });

                if (!buyInformation?.disableCaptcha) {
                  setValue('captchaText', '', { shouldValidate: true });
                  getCaptcha(offerIdentifier);
                }
              });
          } else if (data.paymentMethod === 'creditCard') {
            request({
              url: `/api/v1/OfferPayment/BuyWithCreditCard/${offerIdentifier}`,
              method: 'post',
              baseURL: apiGatewayUrl,
              data: {
                schoolAlias,

                // Contact
                ...contact,

                // Personal
                ...personal,

                // Marketing
                ...marketing,

                // Security
                ...security,

                // Payment
                installments: Number(data.installments),
                creditCardHolderName: data.creditCardHolderName,
                creditCardExpirationMonth: Number(data.creditCardExpirationMonth),
                creditCardExpirationYear: Number(data.creditCardExpirationYear),
                creditCardNumber: data.creditCardNumber,
                creditCardSecurityCode: data.creditCardSecurityCode,
                paymentUrl,
              },
            })
              .then(({ data }: AxiosResponse<PaymentsApiResult>) => {
                const { apiResultData: paymentResponse } = data;

                setPaymentsResponse(paymentResponse);
                goToStep('success');

                eventTracker(
                  {
                    category: 'checkout',
                    action: 'Purchase',
                    value: checkout?.spotValue,
                    transaction_id: paymentResponse?.transactionLocator,
                    product_name: buyInformation?.offerName,
                    customer_email: contact?.customerContactEmail,
                    conversionLabel,
                    schoolAlias,
                  },
                  true
                );

                setTimeout(() => {
                  // TODO: Adicionar upsell
                  if (buyInformation?.offerConclusionPageType === OfferConclusionPageTypeEnum.Default) {
                    navigate(createUrl('offers/success', [offerIdentifier]));
                  } else if (buyInformation?.offerConclusionPageType === OfferConclusionPageTypeEnum.ExternalPage) {
                    window.location.href = buyInformation?.offerExternalUrlCreditCard;
                  }
                }, 10);

                setLoadingEdit(false);
              })
              .catch((error: AxiosError<PaymentsApiResult>) => {
                setLoadingEdit(false);

                let message = 'Ocorreu um erro na comunicação com o serviço de pagamento. Por favor, tente novamente.';

                if (error.response?.data?.apiResultMessage) {
                  message = error.response?.data?.apiResultMessage;
                }

                setAlert({ type: 'error', message });

                if (!buyInformation?.disableCaptcha) {
                  setValue('captchaText', '', { shouldValidate: true });
                  getCaptcha(offerIdentifier);
                }
              });
          } else if (data.paymentMethod === 'boleto') {
            request({
              url: `/api/v1/OfferPayment/BuyWithBoleto/${offerIdentifier}`,
              method: 'post',
              baseURL: apiGatewayUrl,
              data: {
                schoolAlias,

                // Contact
                ...contact,

                // Personal
                ...personal,

                // Marketing
                ...marketing,

                // Security
                ...security,

                // Payment
                installments: Number(data.installments),
                paymentUrl,
              },
            })
              .then(({ data }: AxiosResponse<PaymentsApiResult>) => {
                const { apiResultData: paymentResponse } = data;

                setPaymentsResponse(paymentResponse);
                goToStep('success');

                eventTracker(
                  {
                    category: 'checkout',
                    action: 'Boleto pendente',
                    value: checkout?.spotValue,
                    transaction_id: paymentResponse?.transactionLocator,
                    product_name: buyInformation?.offerName,
                    customer_email: contact?.customerContactEmail,
                    conversionLabel,
                    schoolAlias,
                  },
                  true
                );

                setTimeout(() => {
                  navigate(createUrl('offers/success', [offerIdentifier]));

                  // TODO: Adicionar upsell
                  // if (buyInformation?.offerConclusionPageType === OfferConclusionPageTypeEnum.Default) {
                  // }
                  //  else if (buyInformation?.offerConclusionPageType === OfferConclusionPageTypeEnum.ExternalPage) {
                  //   window.location.href = buyInformation?.offerExernalUrlBoleto;
                  // }
                }, 10);

                setLoadingEdit(false);
              })
              .catch((error: AxiosError<PaymentsApiResult>) => {
                setLoadingEdit(false);

                let message = 'Ocorreu um erro na comunicação com o serviço de pagamento. Por favor, tente novamente.';

                if (error.response?.data?.apiResultMessage) {
                  message = error.response?.data?.apiResultMessage;
                }

                setAlert({ type: 'error', message });

                if (!buyInformation?.disableCaptcha) {
                  setValue('captchaText', '', { shouldValidate: true });
                  getCaptcha(offerIdentifier);
                }

                // if (error.response) {
                //   setAlert({ type: 'error', message: error.response.data?.apiResultMessage });
                // } else if (error.request) {
                //   setAlert({ type: 'error', message: error.request });
                // } else {
                //   setAlert({ type: 'error', message: error.message });
                // }
              });
          } else if (data.paymentMethod === 'pix') {
            request({
              url: `/api/v1/OfferPayment/BuyWithPix/${offerIdentifier}`,
              method: 'post',
              baseURL: apiGatewayUrl,
              data: {
                schoolAlias,

                // Contact
                ...contact,

                // Personal
                ...personal,

                // Marketing
                ...marketing,

                // Security
                ...security,

                // Payment
                paymentUrl,
              },
            })
              .then(({ data }: AxiosResponse<PaymentsApiResult>) => {
                const { apiResultData: paymentResponse } = data;

                setPaymentsResponse(paymentResponse);
                goToStep('success');

                eventTracker(
                  {
                    category: 'checkout',
                    action: 'Pix pendente',
                    value: checkout?.spotValue,
                    transaction_id: paymentResponse?.transactionLocator,
                    product_name: buyInformation?.offerName,
                    customer_email: contact?.customerContactEmail,
                    conversionLabel,
                    schoolAlias,
                  },
                  true
                );

                setTimeout(() => {
                  navigate(createUrl('offers/success', [offerIdentifier]));

                  // TODO: Adicionar upsell
                  // if (buyInformation?.offerConclusionPageType === OfferConclusionPageTypeEnum.Default) {
                  // }
                  // else if (buyInformation?.offerConclusionPageType === OfferConclusionPageTypeEnum.ExternalPage) {
                  //   window.location.href = buyInformation?.offerExternalUrlPix;
                  // }
                }, 10);

                setLoadingEdit(false);
              })
              .catch((error: AxiosError<PaymentsApiResult>) => {
                setLoadingEdit(false);

                let message = 'Ocorreu um erro na comunicação com o serviço de pagamento. Por favor, tente novamente.';

                if (error.response?.data?.apiResultMessage) {
                  message = error.response?.data?.apiResultMessage;
                }

                setAlert({ type: 'error', message });

                if (!buyInformation?.disableCaptcha) {
                  setValue('captchaText', '', { shouldValidate: true });
                  getCaptcha(offerIdentifier);
                }

                // setLoadingEdit(false);

                // if (error.response) {
                //   setAlert({ type: 'error', message: error.response.data?.apiResultMessage });
                // } else if (error.request) {
                //   setAlert({ type: 'error', message: error.request });
                // } else {
                //   setAlert({ type: 'error', message: error.message });
                // }
              });
          }
        })}
      >
        <Box p={{ m: 3, d: 5 }}>
          <Flex flexDirection="column">
            <Typography type="type1" fontWeight="bold" mb={1}>
              <Label htmlFor={`customerName`}>Nome completo</Label>
            </Typography>

            <Input
              data-error={!!errors.customerName}
              id="customerName"
              {...register('customerName', {
                // onChange: (e: ChangeEvent<HTMLInputElement>) => {
                // setInitiateCheckout((prev) => {
                //   if (!tempInitiateCheckout && !prev) {
                //     tempInitiateCheckout = true;

                // eventTracker(
                //   {
                //     category: 'checkout',
                //     action: 'Initiate Checkout',
                //     value: checkout?.spotValue,
                //   },
                //   true
                // );
                //   }

                //   return true;
                // });
                // setValue('customerName', e.currentTarget.value);
                // },

                onBlur: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  sendLead({ customerName: e.currentTarget.value });
                },
              })}
            />

            {!!errors.customerName && <HelperText type="error">{errors.customerName.message}</HelperText>}
          </Flex>

          <Flex flexDirection="column" mt={3}>
            <Flex justifyContent="space-between" mb={1}>
              <Typography type="type1" fontWeight="bold">
                <Label htmlFor={`customerContactEmail`}>E-mail</Label>
              </Typography>

              <Typography type="type0" color="black50">
                Enviaremos seus dados de acesso para este e-mail
              </Typography>
            </Flex>

            {/* <Input
              data-error={!!errors.customerContactEmail}
              id="customerContactEmail2"
              {...register('customerContactEmail', {
                onChange: (e: ChangeEvent<HTMLInputElement>) => {
                  setValue('customerContactEmail', e.currentTarget.value.trim());
                },
                onBlur: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  sendLead({ customerContactEmail: e.currentTarget.value }, true);
                },
              })}
            /> */}

            <InputSuggest
              data-error={!!errors.customerContactEmail}
              id="customerContactEmail"
              {...register('customerContactEmail')}
              value={customerContactEmail}
              handleOnChange={(value: string) => {
                setValue('customerContactEmail', value.trim());
                setUpdate(new Date());
              }}
              handleOnBlur={(email) => {
                sendLead({ customerContactEmail: email }, true);
              }}
            />

            {!!errors.customerContactEmail && <HelperText type="error">{errors.customerContactEmail.message}</HelperText>}
          </Flex>

          {showLoginMessage && (
            <Flex
              style={{ background: 'rgba(0, 191, 190, 0.1)', borderColor: 'rgba(0, 191, 190, 0.3)', borderWidth: 2, borderStyle: 'dashed' }}
              mt={2}
              p={2}
              borderRadius={4}
            >
              <Typography type="type1" fontWeight="bold" color="primary_600">
                Esse e-mail já está cadastrado na plataforma.{' '}
                <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => setLogin({ showLogin: true })}>
                  Clique aqui
                </span>{' '}
                para fazer login.
              </Typography>
            </Flex>
          )}

          {withEMailConfirmation && (
            <Flex flexDirection="column" mt={3}>
              <Flex justifyContent="space-between" mb={1}>
                <Typography type="type1" fontWeight="bold">
                  <Label htmlFor={`customerContactEmailConfirm`}>Repita o e-mail</Label>
                </Typography>
              </Flex>

              <InputSuggest
                data-error={!!errors.customerContactEmailConfirm}
                id="customerContactEmailConfirm"
                {...register('customerContactEmailConfirm')}
                value={customerContactEmailConfirm}
                handleOnChange={(value: string) => {
                  setValue('customerContactEmailConfirm', value.trim());
                  setUpdate(new Date());
                }}
              />

              {/* <Input
              data-error={!!errors.customerContactEmailConfirm}
              id="customerContactEmailConfirm"
              {...register('customerContactEmailConfirm', {
                onChange: (e: ChangeEvent<HTMLInputElement>) => {
                  setValue('customerContactEmailConfirm', e.currentTarget.value.trim());
                },
                onBlur: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  sendLead({ customerContactEmailConfirm: e.currentTarget.value }, true);
                },
              })}
            /> */}

              {!!errors.customerContactEmailConfirm && <HelperText type="error">{errors.customerContactEmailConfirm.message}</HelperText>}
            </Flex>
          )}

          {withPhone && (
            <Flex flexDirection="column" mt={3}>
              <Flex justifyContent="space-between" mb={1}>
                <Typography type="type1" fontWeight="bold">
                  <Label htmlFor={`internationalContactPhone`}>Celular</Label>
                </Typography>
              </Flex>

              <Flex maxWidth={{ m: 280 }} flexWrap="nowrap">
                <Box>
                  <Input
                    data-error={!!errors.customerContactPhone}
                    id="customerContactPhone"
                    {...register('customerContactPhone', {
                      onChange: (e: ChangeEvent<HTMLInputElement>) => {
                        setValue('customerContactPhone', !internationalContactPhone ? phoneMask(e.currentTarget.value) : e.currentTarget.value);
                      },
                      onBlur: (e: React.KeyboardEvent<HTMLInputElement>) => {
                        sendLead({ customerContactPhone: e.currentTarget.value });
                      },
                    })}
                    maxLength={!internationalContactPhone ? 15 : undefined}
                  />
                </Box>

                {/* <InternationalContactPhoneWrapper>
                <Checkbox
                  color="black30"
                  colorActive="black50"
                  checked={internationalContactPhone}
                  {...register('internationalContactPhone', {
                    onChange: (e: ChangeEvent<HTMLInputElement>) => {
                      setValue('internationalContactPhone', e.target.checked, {
                        shouldValidate: true,
                      });
                    },
                  })}
                />

                <Typography type="type1" fontWeight="bold" color="black50" ml={1} style={{ cursor: 'pointer' }}>
                  Internacional
                </Typography>
              </InternationalContactPhoneWrapper> */}
              </Flex>

              {!!errors.customerContactPhone && <HelperText type="error">{errors.customerContactPhone.message}</HelperText>}
            </Flex>
          )}

          {/* customerTaxId */}
          {withTaxId && (
            <Flex flexDirection="column" mt={3}>
              <Flex justifyContent="space-between" flexDirection={{ m: 'column', t: 'row' }} mb={1}>
                <Typography type="type1" fontWeight="bold">
                  <Label htmlFor={`customerTaxId`}>{internationalContactPhone ? 'Número de documento' : 'CPF/CNPJ'}</Label>
                </Typography>

                {!buyInformation?.freeOffer && (
                  <>
                    {!internationalContactPhone && (
                      <Typography type="type0" color="black50" mt={{ m: 1, t: 0 }}>
                        Precisamos dessa informação para emissão de Nota Fiscal
                      </Typography>
                    )}
                  </>
                )}
              </Flex>

              <Input
                data-error={!!errors.customerTaxId}
                id="customerTaxId"
                type="text"
                placeholder="Digite apenas números"
                {...register('customerTaxId', {
                  onChange: (e: ChangeEvent<HTMLInputElement>) => {
                    let currentCustomerType = CustomersTypes.Person;

                    if (!internationalContactPhone) {
                      const taxId = e.target.value.replace(/\D/g, '');

                      setValue('customerTaxId', taxId ? String(cpfCnpjMask(taxId)) : taxId);

                      if (taxId.length > 11) {
                        currentCustomerType = CustomersTypes.Company;
                      }

                      if (currentCustomerType !== customerType) {
                        setValue('customerType', currentCustomerType, { shouldValidate: true });
                        setCheckout({ customerType: currentCustomerType });
                      }
                    } else {
                      setValue('customerTaxId', e.target.value);
                    }
                  },
                  onBlur: (e: React.KeyboardEvent<HTMLInputElement>) => {
                    sendLead({ customerTaxId: e.currentTarget.value }, false, true);
                  },
                })}
                maxLength={internationalContactPhone ? 100 : 20}
              />

              {!!errors.customerTaxId && <HelperText type="error">{errors.customerTaxId.message}</HelperText>}
            </Flex>
          )}
          {/* /customerTaxId */}
        </Box>

        <>
          {!buyInformation?.freeOffer && (
            <>
              <Hr m={0} />
              <Box p={{ m: 3, d: 5 }}>
                <PaymentsMethods
                  paymentMethod={paymentMethod}
                  isInternational={internationalContactPhone}
                  onChange={(receivedPaymentMethod: PaymentsMethod, receivedInstallments: number, receivedValue: number) => {
                    sendLead({
                      paymentMethod: receivedPaymentMethod,
                      installments: receivedInstallments,
                      value: receivedValue,
                    });

                    setValue('showAddress', receivedPaymentMethod === 'boleto' || withAddress, {
                      shouldValidate: true,
                    });

                    // setValue('installments', receivedInstallments, { shouldValidate: true });
                    // setValue('value', receivedValue, { shouldValidate: true });

                    // setCheckout({ paymentMethod, installments, value });
                  }}
                />
                {paymentMethod === 'creditCard' && (
                  <>
                    {/* creditCardNumber */}
                    <Flex flexDirection="column" mt={4}>
                      <Typography type="type1" fontWeight="bold" mb={1}>
                        <Label htmlFor={`creditCardNumber`}>Número do cartão</Label>
                      </Typography>

                      <Flex position="relative">
                        <Input
                          data-error={!!errors.creditCardNumber}
                          id="creditCardNumber"
                          type="text"
                          placeholder="Digite apenas números"
                          {...register('creditCardNumber', {
                            onChange: (e: ChangeEvent<HTMLInputElement>) => {
                              const cardNumber = e.currentTarget.value.replace(/\D/g, '');

                              setValue('creditCardNumber', cardNumber, {
                                shouldValidate: true,
                              });
                            },
                            // onBlur: (e: React.KeyboardEvent<HTMLInputElement>) => {
                            //   setCheckout({ [e.currentTarget.name]: e.currentTarget.value });
                            // },
                          })}
                          maxLength={20}
                        />

                        {creditCardBrand.flag !== 'default' && (
                          <CreditCardFlag src={`/images/paymentsFlags/${creditCardBrand.image}`} width={30} alt={creditCardBrand.name} />
                        )}
                      </Flex>

                      {!!errors.creditCardNumber && <HelperText type="error">{errors.creditCardNumber.message}</HelperText>}
                    </Flex>
                    {/* /creditCardNumber */}

                    {/* creditCardHolderName */}
                    <Flex flexDirection="column" mt={4}>
                      <Typography type="type1" fontWeight="bold" mb={1}>
                        <Label htmlFor={`creditCardHolderName`}>Nome do titular do cartão (Assim como está impresso no cartão)</Label>
                      </Typography>

                      <Input
                        data-error={!!errors.creditCardHolderName}
                        id="creditCardHolderName"
                        {...register('creditCardHolderName', {
                          onBlur: (e: React.KeyboardEvent<HTMLInputElement>) => {
                            // setCheckout({ [e.currentTarget.name]: e.currentTarget.value });
                          },
                        })}
                      />

                      {!!errors.creditCardHolderName && <HelperText type="error">{errors.creditCardHolderName.message}</HelperText>}
                    </Flex>
                    {/* /creditCardHolderName */}

                    <Flex mt={4} flexDirection={{ m: 'column', d: 'row' }} justifyContent="space-between">
                      {/* creditCardExpirationBOX */}
                      <Flex flexDirection="column">
                        <Typography type="type1" fontWeight="bold" mb={1}>
                          <Label htmlFor={`creditCardExpirationMonth`}>Validade do cartão</Label>
                        </Typography>

                        <Flex flexWrap="nowrap">
                          {/* creditCardExpirationMonth */}
                          <Box maxWidth="50%" pr={1}>
                            <Select
                              data-error={!!errors.creditCardExpirationMonth}
                              defaultValue={creditCardExpirationMonth}
                              {...register('creditCardExpirationMonth', {
                                onChange: (e: ChangeEvent<HTMLSelectElement>) => {
                                  setValue('creditCardExpirationMonth', Number(e.target.value));
                                  // setCheckout({ creditCardExpirationMonth: Number(e.currentTarget.value) });
                                },
                              })}
                            >
                              <option>Mês</option>

                              {Array(12)
                                .fill(null)
                                .map((_, month) => {
                                  return (
                                    <option key={`creditCardExpirationMonth-${month.toString()}`} value={month + 1}>
                                      {(month + 1).toString().padStart(2, '0')}
                                    </option>
                                  );
                                })}
                            </Select>
                          </Box>
                          {/* /creditCardExpirationMonth */}

                          {/* creditCardExpirationYear */}
                          <Box maxWidth="50%" pl={1}>
                            <Select
                              data-error={!!errors.creditCardExpirationYear}
                              defaultValue={creditCardExpirationYear}
                              {...register('creditCardExpirationYear', {
                                onChange: (e: ChangeEvent<HTMLSelectElement>) => {
                                  setValue('creditCardExpirationYear', Number(e.target.value));
                                },
                              })}
                            >
                              <option>Ano</option>

                              {Array(20)
                                .fill(null)
                                .map((_, index) => {
                                  const year = new Date().getFullYear();
                                  return (
                                    <option key={`creditCardExpirationYear-${index.toString()}`} value={year + index}>
                                      {year + index}
                                    </option>
                                  );
                                })}
                            </Select>
                          </Box>
                          {/* /creditCardExpirationYear */}
                        </Flex>

                        {!!(errors.creditCardExpirationMonth || errors.creditCardExpirationYear) && (
                          <HelperText type="error">
                            {errors.creditCardExpirationMonth
                              ? errors.creditCardExpirationMonth.message
                              : errors.creditCardExpirationYear
                              ? errors.creditCardExpirationYear.message
                              : 'Ocorreu um erro inesperado.'}
                          </HelperText>
                        )}
                      </Flex>
                      {/* /creditCardExpirationBOX */}

                      {/* creditCardSecurityCode */}
                      <Flex flexDirection="column" mt={{ m: 4, d: 0 }} pl={{ m: 0, d: 6 }}>
                        <Typography type="type1" fontWeight="bold" mb={1}>
                          <Label htmlFor={`creditCardSecurityCode`}>Código de segurança (CVV)</Label>
                        </Typography>

                        <Input
                          data-error={!!errors.creditCardSecurityCode}
                          id="creditCardSecurityCode"
                          {...register('creditCardSecurityCode', {
                            onBlur: (e: React.KeyboardEvent<HTMLInputElement>) => {
                              // setCheckout({ [e.currentTarget.name]: e.currentTarget.value });
                            },
                          })}
                          maxLength={4}
                        />

                        {!!errors.creditCardSecurityCode && <HelperText type="error">{errors.creditCardSecurityCode.message}</HelperText>}
                      </Flex>
                      {/* /creditCardSecurityCode */}
                    </Flex>
                  </>
                )}
                {paymentMethod === 'boleto' && (
                  <>
                    <Box mt={4}>
                      <Typography type="type1" fontWeight="bold">
                        Atente-se aos detalhes
                      </Typography>
                      <br />

                      <Typography type="type0" lineHeight={1.3} color="black50" mt={2}>
                        Boleto (somente à vista)
                      </Typography>

                      <Typography type="type0" lineHeight={1.3} color="black50" mt={1}>
                        Pagamentos com Boleto Bancário levam até 3 dias úteis para serem compensados e então terem os produtos liberados
                      </Typography>

                      <Typography type="type0" lineHeight={1.3} color="black50" mt={1}>
                        Atente-se ao vencimento do boleto. Você pode pagar o boleto em qualquer banco ou casa lotérica até o dia do vencimento
                      </Typography>

                      <Typography type="type0" lineHeight={1.3} color="black50" mt={1}>
                        Depois do pagamento, verifique seu e-mail para receber os dados de acesso ao produto (verifique também a caixa de SPAM)
                      </Typography>
                    </Box>
                  </>
                )}
                {paymentMethod === 'pix' && (
                  <>
                    <Box mt={5}>
                      <Row gutter={50}>
                        <Col width={{ m: 1, d: 1 / 3 }}>
                          <Flex flexDirection="column" alignItems="center" textAlign="center">
                            <Icon icon="timer" />

                            <Typography type="type0" color="gray_500" mt={3}>
                              Faça seu pagamento em poucos segundos sem custos adicionais.
                            </Typography>
                          </Flex>
                        </Col>

                        <Col width={{ m: 1, d: 1 / 3 }} mt={{ m: 4, d: 0 }}>
                          <Flex flexDirection="column" alignItems="center" textAlign="center">
                            <Icon icon="qrcode" />

                            <Typography type="type0" color="gray_500" mt={3}>
                              Para pagar basta abrir o aplicativo do seu banco, procurar pelo PIX e escanear o QRcode.
                            </Typography>
                          </Flex>
                        </Col>

                        <Col width={{ m: 1, d: 1 / 3 }} mt={{ m: 4, d: 0 }}>
                          <Flex flexDirection="column" alignItems="center" textAlign="center">
                            <Icon icon="shieldLock" />

                            <Typography type="type0" color="gray_500" mt={3}>
                              O pagamento com PIX foi desenvolvido pelo Banco Central para facilitar suas compras.
                            </Typography>
                          </Flex>
                        </Col>
                      </Row>
                    </Box>
                  </>
                )}

                {getInstallments()?.length > 1 && !buyInformation?.isRecurrence && (
                  <>
                    {/* installments */}
                    <Flex flexDirection="column" mt={4}>
                      <Typography type="type1" fontWeight="bold" mb={1}>
                        <Label htmlFor={`installments`}>Opções de parcelamento</Label>
                      </Typography>

                      <Select
                        data-error={!!errors.installments}
                        value={installments}
                        {...register('installments', {
                          onChange: (e: ChangeEvent<HTMLSelectElement>) => {
                            setValue('installments', Number(e.target.value), {
                              shouldValidate: true,
                            });

                            const paymentValue = getInstallments()
                              .filter((paymentValue: PaymentValuesItem) => paymentValue.installments === Number(e.target.value))
                              .pop();

                            sendLead({
                              installments: paymentValue?.installments,
                              value: paymentValue?.totalValue,
                            });
                          },
                        })}
                        disabled={internationalContactPhone}
                      >
                        {getInstallments()?.map((installment, index) => {
                          return (
                            <option
                              key={`installments-${installment.installments.toString()}`}
                              value={installment.installments}
                              // selected={installment.installments === installments}
                            >
                              {`${installment.installments}x de ${brazilianRealFormat(installment.installmentValue)} ${
                                installment.installments === 1 ? `(sem juros)` : `(com juros)`
                              }`}
                            </option>
                          );
                        })}
                      </Select>

                      {!!errors.installments && <HelperText type="error">{errors.installments.message}</HelperText>}
                    </Flex>
                    {/* /installments */}
                  </>
                )}
              </Box>
            </>
          )}

          <Hr color="gray_200" size="2px" />

          <Box px={{ m: 3, d: 5 }} pb={{ m: 3, d: 5 }}>
            {/* customerBirthOrFoundation */}
            {withBirthDate && (
              <Flex flexDirection="column" mt={4}>
                <Typography type="type1" fontWeight="bold" mb={1}>
                  <Label htmlFor={`customerBirthOrFoundation`}>
                    {customerType === CustomersTypes.Person ? 'Data de nascimento' : 'Data de abertura da empresa'}
                  </Label>
                </Typography>

                <Flex maxWidth={180}>
                  <Input
                    data-error={!!errors.customerBirthOrFoundation}
                    id="customerBirthOrFoundation"
                    type="text"
                    placeholder="Digite apenas números"
                    {...register('customerBirthOrFoundation', {
                      onChange: (e: ChangeEvent<HTMLInputElement>) => {
                        setValue('customerBirthOrFoundation', String(dateMask2(e.target.value.toString())));
                      },
                      onBlur: (e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.currentTarget.value && e.currentTarget.value.length === 10) {
                          sendLead({
                            customerBirthOrFoundation: e.currentTarget.value,
                          });
                        }
                      },
                    })}
                    maxLength={10}
                  />
                </Flex>
                {!!errors.customerBirthOrFoundation && <HelperText type="error">{errors.customerBirthOrFoundation.message}</HelperText>}
              </Flex>
            )}
            {/* /customerBirthOrFoundation */}

            {showAddress && (
              <>
                {/* customerZipCode */}
                <Flex flexDirection="column" mt={4}>
                  <Typography type="type1" fontWeight="bold" mb={1}>
                    <Label htmlFor={`customerZipCode`}>CEP</Label>
                  </Typography>

                  <Flex maxWidth={120} position="relative">
                    <Input
                      data-error={!!errors.customerZipCode}
                      id="customerZipCode"
                      type="text"
                      {...register('customerZipCode', {
                        onChange: async (e: ChangeEvent<HTMLInputElement>) => {
                          const customerZipCode = e.target.value.replace(/\D/g, '');

                          setValue('customerZipCode', !internationalContactPhone ? String(cepMask(customerZipCode)) : customerZipCode);

                          if (!internationalContactPhone && customerZipCode.length === 8) {
                            setLoadingEdit(true);

                            await getAddressByZipcode(customerZipCode)
                              .then((address) => {
                                if (address) {
                                  setValue('customerAddressLine1', address.address);
                                  setValue('customerNeighborhood', address.district);
                                  setValue('customerCity', address.city);
                                  setValue('customerState', address.state);

                                  sendLead(
                                    {
                                      customerState: address.state,
                                      customerCity: address.city,
                                    },
                                    false,
                                    true
                                  );

                                  setFocus('customerAddressNumber');
                                }
                              })
                              .catch(() => {
                                setValue('customerAddressLine1', '');
                                setValue('customerNeighborhood', '');
                                setValue('customerCity', '');
                                setValue('customerState', '');

                                setFocus('customerAddressLine1');
                              })
                              .finally(() => {
                                setLoadingEdit(false);
                              });
                          }
                        },
                        onBlur: (e: React.KeyboardEvent<HTMLInputElement>) => {
                          setCheckout({ [e.currentTarget.name]: e.currentTarget.value });
                        },
                      })}
                      maxLength={9}
                    />
                  </Flex>

                  {!!errors.customerZipCode && <HelperText type="error">{errors.customerZipCode.message}</HelperText>}
                </Flex>
                {/* /customerZipCode */}

                <Flex mt={4} flexDirection={{ m: 'column', t: 'row' }} justifyContent="space-between">
                  {/* customerAddressLine1 */}
                  <Flex flexDirection="column">
                    <Typography type="type1" fontWeight="bold" mb={1}>
                      <Label htmlFor={`customerAddressLine1`}>Endereço</Label>
                    </Typography>

                    <Input
                      data-error={!!errors.customerAddressLine1}
                      id="customerAddressLine1"
                      {...register('customerAddressLine1', {
                        onBlur: (e: React.KeyboardEvent<HTMLInputElement>) => {
                          // setCheckout({ [e.currentTarget.name]: e.currentTarget.value });
                        },
                      })}
                    />

                    {!!errors.customerAddressLine1 && <HelperText type="error">{errors.customerAddressLine1.message}</HelperText>}
                  </Flex>
                  {/* /customerAddressLine1 */}

                  {/* customerAddressNumber */}
                  <Flex maxWidth={130} flexDirection="column" mt={{ m: 4, t: 0 }} ml={{ m: 0, t: 6 }}>
                    <Typography type="type1" fontWeight="bold" mb={1}>
                      <Label htmlFor={`customerAddressNumber`}>Número</Label>
                    </Typography>

                    <Input
                      data-error={!!errors.customerAddressNumber}
                      id="customerAddressNumber"
                      {...register('customerAddressNumber', {
                        onBlur: (e: React.KeyboardEvent<HTMLInputElement>) => {
                          // setCheckout({ [e.currentTarget.name]: e.currentTarget.value });
                        },
                      })}
                    />

                    {!!errors.customerAddressNumber && <HelperText type="error">{errors.customerAddressNumber.message}</HelperText>}
                  </Flex>
                  {/* /customerAddressNumber */}
                </Flex>

                {/* customerAddressLine2 */}
                <Flex flexDirection="column" mt={4}>
                  <Typography type="type1" fontWeight="bold" mb={1}>
                    <Label htmlFor={`customerAddressLine2`}>Complemento</Label>
                  </Typography>

                  <Input
                    data-error={!!errors.customerAddressLine2}
                    id="customerAddressLine2"
                    {...register('customerAddressLine2', {
                      onBlur: (e: React.KeyboardEvent<HTMLInputElement>) => {
                        // setCheckout({ [e.currentTarget.name]: e.currentTarget.value });
                      },
                    })}
                  />

                  {!!errors.customerAddressLine2 && <HelperText type="error">{errors.customerAddressLine2.message}</HelperText>}
                </Flex>
                {/* /customerAddressLine2 */}

                {/* customerNeighborhood */}
                <Flex flexDirection="column" mt={4}>
                  <Typography type="type1" fontWeight="bold" mb={1}>
                    <Label htmlFor={`customerNeighborhood`}>Bairro</Label>
                  </Typography>

                  <Input
                    data-error={!!errors.customerNeighborhood}
                    id="customerNeighborhood"
                    {...register('customerNeighborhood', {
                      onBlur: (e: React.KeyboardEvent<HTMLInputElement>) => {
                        // setCheckout({ [e.currentTarget.name]: e.currentTarget.value });
                      },
                    })}
                  />

                  {!!errors.customerNeighborhood && <HelperText type="error">{errors.customerNeighborhood.message}</HelperText>}
                </Flex>
                {/* /customerNeighborhood */}

                <Flex mt={4} flexDirection={{ m: 'column', t: 'row' }} justifyContent="space-between">
                  {/* customerCity */}
                  <Flex flexDirection="column">
                    <Typography type="type1" fontWeight="bold" mb={1}>
                      <Label htmlFor={`customerCity`}>Cidade</Label>
                    </Typography>

                    <Input
                      data-error={!!errors.customerCity}
                      id="customerCity"
                      {...register('customerCity', {
                        onBlur: (e: React.KeyboardEvent<HTMLInputElement>) => {
                          if (e.currentTarget.value) {
                            sendLead(
                              {
                                customerCity: e.currentTarget.value,
                              },
                              false,
                              true
                            );
                          }
                        },
                      })}
                    />

                    {!!errors.customerCity && <HelperText type="error">{errors.customerCity.message}</HelperText>}
                  </Flex>
                  {/* /customerCity */}

                  {/* customerState */}
                  <Flex maxWidth={100} flexDirection="column" mt={{ m: 4, t: 0 }} ml={{ m: 0, t: 6 }}>
                    <Typography type="type1" fontWeight="bold" mb={1}>
                      <Label htmlFor={`customerState`}>Estado</Label>
                    </Typography>

                    <Input
                      data-error={!!errors.customerState}
                      id="customerState"
                      {...register('customerState', {
                        onBlur: (e: React.KeyboardEvent<HTMLInputElement>) => {
                          if (e.currentTarget.value) {
                            sendLead(
                              {
                                customerState: e.currentTarget.value,
                              },
                              false,
                              true
                            );
                          }
                        },
                      })}
                      maxLength={2}
                    />

                    {!!errors.customerState && <HelperText type="error">{errors.customerState.message}</HelperText>}
                  </Flex>
                  {/* /customerState */}
                </Flex>
              </>
            )}

            {!!melverAlert.message && (
              <Flex bg={melverAlert.type === 'error' ? 'alert_400' : 'success_400'} mt={{ m: 4, d: 5 }} p={3} borderRadius={4}>
                <Typography type="type1" color="white">
                  {melverAlert.message}
                </Typography>
              </Flex>
            )}

            {!buyInformation?.freeOffer && (
              <>
                <Flex mt={{ m: 4, d: 5 }} flexDirection="column">
                  <Typography color="dark_350" fontWeight="bold">
                    Resumo da compra
                  </Typography>

                  <Hr size="2px" />

                  <Flex py={1} justifyContent="space-between" flexWrap="nowrap">
                    <Typography color="black30">{buyInformation?.offerName}</Typography>

                    <Typography color="black30" style={{ whiteSpace: 'nowrap' }} ml={3}>
                      {installments}x de {brazilianRealFormat(getInstallmentsValue())}
                    </Typography>
                  </Flex>

                  <Hr size="1px" />
                </Flex>
              </>
            )}

            {!disableCaptcha && (
              <Flex flexDirection="column" mt={{ m: 2, d: 3 }}>
                <Typography type="type1" fontWeight="bold">
                  Insira os caracteres exibidos na imagem abaixo:
                </Typography>

                <Flex mt={1}>
                  <img src={captcha?.captchaBase64} alt="" />
                  <ButtonIcon
                    ml={2}
                    type="button"
                    onClick={() => {
                      setValue('captchaText', '', { shouldValidate: true });
                      getCaptcha(offerIdentifier);
                    }}
                    icon="refresh"
                    bg="transparent"
                    color="dark_350"
                  />
                </Flex>

                <Flex maxWidth={{ m: '', d: 200 }} mt={2}>
                  <Input data-error={!!errors.captchaText} id="captchaText" {...register('captchaText')} />
                </Flex>

                {!!errors.captchaText && <HelperText type="error">{errors.captchaText.message}</HelperText>}
              </Flex>
            )}

            <Flex mt={{ m: 4, d: 5 }}>
              <input
                type="checkbox"
                id="hasOptin"
                checked={hasOptin}
                {...register('hasOptin')}
                style={{ position: 'absolute', width: 1, height: 1, visibility: 'hidden' }}
              />

              {isProgramXP && (
                <>
                  <Flex flexWrap="nowrap" mb={3}>
                    <Checkbox
                      id="optin"
                      color="black50"
                      checkboxSize="small"
                      checked={optin}
                      {...register('optin', {
                        onChange: (e: ChangeEvent<HTMLInputElement>) => {
                          setValue('optin', e.target.checked, { shouldValidate: true });
                        },
                      })}
                    />

                    <Flex ml="5px" pt="1px">
                      <Typography type="type1" color="black80">
                        <Label htmlFor="optin">
                          Declaro ter lido e aceito o{' '}
                          <LinkTerms
                            href="https://www.melver.com.br/termos-e-politicas/termo-de-adesao-para-o-plano-de-formacao-assessores-xp"
                            target="_blank"
                          >
                            termo de adesão
                          </LinkTerms>{' '}
                          e a{' '}
                          <LinkTerms href="https://www.melver.com.br/termos-e-politicas/politica-de-privacidade/" target="_blank">
                            política de privacidade
                          </LinkTerms>
                          .
                        </Label>
                      </Typography>

                      {!!errors.optin && <HelperText type="error">{errors.optin.message}</HelperText>}
                    </Flex>
                  </Flex>
                </>
              )}
              <Button square type="submit" width="100%" py={{ m: 3, d: 4 }} px={{ m: 3, d: 4 }}>
                {submitLabel}
              </Button>
            </Flex>

            {!buyInformation?.freeOffer && (
              <Flex justifyContent="space-between" flexWrap="nowrap" mt={3}>
                <Flex width="50%" alignItems="center" flexWrap="nowrap" mr={1}>
                  <WarrantyIcon icon="shieldCheck" />

                  <Typography type="type1" fontWeight="bold" color="gray_500" ml={2}>
                    Garantia de {buyInformation?.warrantyDays} dias após a compra
                  </Typography>
                </Flex>

                <Flex width="50%" alignItems="center" flexWrap="nowrap" ml={1}>
                  <LockIcon icon="shieldLock" />

                  <Typography type="type1" fontWeight="bold" color="gray_500" ml={2}>
                    Compra 100% segura
                  </Typography>
                </Flex>
              </Flex>
            )}

            <PaymentsCopy freeOffer={buyInformation?.freeOffer} />
          </Box>
        </>
      </form>
    </Box>
  );
};

export default Checkout;
